import React from "react"
import { Label } from "@/components/shadcn/ui/label"
import { SelectField as SelectFieldType } from "@/models/new/forms.types"
import { ResponseValue, SelectResponse } from "@/models/new/formResponse.types"
import { SelectOption } from "@/types/select-options"
import { MultiSelect } from "@/components/MultiSelect"

interface Props {
  field: SelectFieldType
  value?: ResponseValue
  onChange: (fieldId: string, value: ResponseValue) => void
  listOptions?: Record<string, SelectOption[]>
}

const SelectField = (props: Props) => {
  const { field, value, onChange, listOptions } = props

  const currentValue =
    value?.type === "select"
      ? (value as SelectResponse).value
      : field.selectionType === "single"
        ? ""
        : []

  const options = React.useMemo(() => {
    const usersList = field.options.source === "organization" && field.options.list === "users"
    const optionsKey = usersList
      ? "organization.users"
      : `${field.options.source}:${field.options.list}`

    return listOptions?.[optionsKey] || listOptions?.[field.options.list] || []
  }, [field.options, listOptions])

  const handleSingleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(field.id, {
      fieldName: field.name,
      type: "select",
      selectionType: "single",
      value: e.target.value,
    })
  }

  const handleMultiChange = (values: string[]) => {
    onChange(field.id, {
      fieldName: field.name,
      type: "select",
      selectionType: "multi",
      value: values,
    })
  }

  const multiSelectOptions = options.map((option) => ({
    value: String(option.id),
    label: option.value,
  }))

  if (field.selectionType === "multi") {
    return (
      <div className="w-1/2 p-4">
        <Label>{field.label}</Label>
        <div className="relative">
          <MultiSelect
            options={multiSelectOptions}
            onValueChange={handleMultiChange}
            value={Array.isArray(currentValue) ? currentValue : []}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="w-1/2 p-4">
      <div>
        <Label>{field.label}</Label>
        <select
          value={typeof currentValue === "string" ? currentValue : ""}
          onChange={handleSingleChange}
          className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
        >
          <option value="">Select an option</option>
          {options.map((option) => (
            <option key={String(option.id)} value={String(option.id)}>
              {option.value}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export default SelectField
